import { useWalkthroughLaunch } from '@/composables/useWalkthroughLaunch';
import { useTutorialsPanelStore } from '@/stores/useTutorialsPanelStore';
import { onboardingTopicContentTypes } from '@/constants/onboardingChecklist';
import { canLaunchOnboardingTopic, findOnboardingTopic } from '@/composables/useOnboardingTopics';
import { waitForPendingSaves } from '@/composables/useSaveInFlightGuard';

/**
 * The one click-dispatch point behind both checklist surfaces (`GetStarted/Widget.vue` and
 * `Tutorials/Panel.vue`): reads a topic's content type and starts the matching thing, so
 * neither surface carries its own copy of the branch.
 *
 * Marking the checklist item complete is deliberately not here — a walkthrough credits its
 * topic from `onFinalStepViewed` (`useWalkthroughLaunch.js`) and a static modal from its last
 * slide displaying (`Tutorials/StaticModal.vue`), so completion can't be missed by a surface
 * that forgets to wire it.
 */
export const useOnboardingTopicLaunch = () => {
  const { launchWalkthrough } = useWalkthroughLaunch();
  const tutorialsPanelStore = useTutorialsPanelStore();

  const launchTopic = async (topicId, { source } = {}) => {
    if (!canLaunchOnboardingTopic(topicId)) {
      return;
    }

    if (onboardingTopicContentTypes[topicId] !== 'walkthrough') {
      tutorialsPanelStore.openStaticTutorial(topicId);
      return;
    }

    await launchWalkthrough(findOnboardingTopic(topicId), {
      source,
      // Completion only: a carrier who cancelled out may have done so to use that page.
      // Waits for any tracked save first so this navigation can't abort it.
      onComplete: async () => {
        await waitForPendingSaves();
        navigateTo('/');
      },
    });
  };

  return { canLaunchTopic: canLaunchOnboardingTopic, launchTopic };
};
